// EditProfile.js
import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import Cropper from "react-easy-crop";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";

/* -------------------- image helpers -------------------- */
// createImage: returns HTMLImageElement after load
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", (err) => reject(err));
    // object URLs and same-origin images don't need crossOrigin, but setting it doesn't harm for remote images
    img.crossOrigin = "anonymous";
    img.src = url;
  });

const getRadianAngle = (degreeValue) => (degreeValue * Math.PI) / 180;

/**
 * rotateSize: returns the bounding box size of a rotated rectangle
 */
const rotateSize = (width, height, rotation) => {
  const rot = getRadianAngle(rotation);
  return {
    width: Math.abs(Math.cos(rot) * width) + Math.abs(Math.sin(rot) * height),
    height:
      Math.abs(Math.sin(rot) * width) + Math.abs(Math.cos(rot) * height),
  };
};

/**
 * getCroppedImg:
 * - imageSrc: url/objectURL of source image
 * - pixelCrop: croppedAreaPixels from react-easy-crop
 * - rotation: rotation in degrees
 *
 * Returns a Blob (image/jpeg).
 */
const getCroppedImg = async (imageSrc, pixelCrop, rotation = 0) => {
  const image = await createImage(imageSrc);
  const rotRad = getRadianAngle(rotation);

  // calculate bounding box of rotated image
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
    image.width,
    image.height,
    rotation
  );

  // create a canvas of the final crop size
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(pixelCrop.width));
  canvas.height = Math.max(1, Math.round(pixelCrop.height));
  const ctx = canvas.getContext("2d");

  // perform transforms so that the area we want to crop ends up at (0,0) on the final canvas
  // Steps:
  // 1) translate the context so that the top-left of the crop rectangle in bounding-box coordinates becomes (0,0)
  // 2) translate to the center of bounding box, rotate, then move origin to draw the image correctly
  ctx.translate(-pixelCrop.x, -pixelCrop.y);
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.translate(-image.width / 2, -image.height / 2);

  // draw the original image (rotation & translations applied)
  ctx.drawImage(image, 0, 0);

  // return blob
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Canvas is empty"));
          return;
        }
        resolve(blob);
      },
      "image/jpeg",
      0.9
    );
  });
};
/* ------------------------------------------------------- */

export default function AdminProfile() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [photo, setPhoto] = useState(null); // preview URL (string)
  const [photoFile, setPhotoFile] = useState(null); // File or Blob ready to upload
  const [loading, setLoading] = useState(false);

  // cropper states
  const [cropMode, setCropMode] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const token = sessionStorage.getItem("token");
  const navigate = useNavigate();

  // Redirect if not authenticated
  useEffect(() => {
    if (!token) {
      toast.error("You must be logged in to access this page.");
      navigate("/");
    }
  }, [token, navigate]);

  // Load Bootstrap & icons (keeps your previous approach)
  useEffect(() => {
    const bootstrapLink = document.createElement("link");
    bootstrapLink.rel = "stylesheet";
    bootstrapLink.href =
      "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css";
    document.head.appendChild(bootstrapLink);

    const iconsLink = document.createElement("link");
    iconsLink.rel = "stylesheet";
    iconsLink.href =
      "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css";
    document.head.appendChild(iconsLink);

    return () => {
      bootstrapLink.remove();
      iconsLink.remove();
    };
  }, []);

  // Fetch user profile
  useEffect(() => {
    if (token) {
      fetch(`${API_BASE_URL}/api/users/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((res) => {
          if (res.status === 401) {
            toast.error("Session expired. Please login again.");
            navigate("/");
            return null;
          }
          return res.json();
        })
        .then((data) => {
          if (data?.email) setEmail(data.email);
          if (data?.name) setName(data.name);
        })
        .catch(() => toast.error("Failed to load profile"));
    }
  }, [token, navigate]);

  // Fetch employee photo
  useEffect(() => {
    if (token) {
      fetch(`${API_BASE_URL}/api/employees/me/photo`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((res) => {
          if (!res.ok) throw new Error("No photo");
          return res.blob();
        })
        .then((blob) => {
          if (blob.size > 0) {
            const url = URL.createObjectURL(blob);
            setPhoto((prev) => {
              // revoke old objectURL if we created one earlier
              if (prev && prev.startsWith("blob:")) URL.revokeObjectURL(prev);
              return url;
            });
          }
        })
        .catch(() => {
          // no photo — keep placeholder
        });
    }
    // cleanup on unmount: revoke created object URL
    return () => {
      if (photo && photo.startsWith("blob:")) {
        URL.revokeObjectURL(photo);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // handle file selection: open crop modal
  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const url = URL.createObjectURL(file);

    // revoke previous objectURL if we created it
    setPhoto((prev) => {
      if (prev && prev.startsWith("blob:")) URL.revokeObjectURL(prev);
      return url;
    });

    setPhotoFile(file);
    setCropMode(true);
    setZoom(1);
    setRotation(0);
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Save crop: create a Blob file and set preview + photoFile
  const handleCropSave = async () => {
    if (!photo || !croppedAreaPixels) {
      toast.error("No image to crop");
      return;
    }
    try {
      const blob = await getCroppedImg(photo, croppedAreaPixels, Number(rotation));
      const croppedFile = new File([blob], (photoFile && photoFile.name) || "photo.jpg", {
        type: "image/jpeg",
      });

      // set preview URL for cropped image
      const newUrl = URL.createObjectURL(blob);
      setPhoto((prev) => {
        if (prev && prev.startsWith("blob:")) URL.revokeObjectURL(prev);
        return newUrl;
      });

      setPhotoFile(croppedFile);
      setCropMode(false);
      toast.success("Image cropped (preview ready). Click Update to save.");
    } catch (err) {
      console.error(err);
      toast.error("Failed to crop image.");
    }
  };

  // Submit profile updates (name/password + optional photo upload)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (oldPassword && newPassword && oldPassword === newPassword) {
      toast.error("New password cannot be the same as old password");
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${API_BASE_URL}/api/users/me`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name, oldPassword, newPassword }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || "Profile updated successfully!");
      } else {
        toast.error(data.error || "Update failed");
      }
    } catch (err) {
      toast.error("Error updating profile");
    }

    // Upload photo if selected
    if (photoFile) {
      const formData = new FormData();
      formData.append("photo", photoFile);
      try {
        const res = await fetch(`${API_BASE_URL}/api/employees/me/photo`, {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        if (res.ok) {
          toast.success("Photo updated successfully!");
        } else {
          toast.error("Failed to update photo");
        }
      } catch (err) {
        toast.error("Error uploading photo");
      }
    }

    setLoading(false);
  };



  return (

     <>
      <AdminNavbar />
    <div className="container mt-5" style={{ maxWidth: "850px" }}>
      {/* Cropper modal */}
      {cropMode && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center"
          style={{ background: "rgba(0,0,0,0.7)", zIndex: 1050 }}
        >
          <div
            className="bg-white rounded-4 p-3 shadow-lg d-flex flex-column align-items-center"
            style={{ width: "92%", maxWidth: 640, height: 540 }}
          >
            <div style={{ position: "relative", width: "100%", height: 360 }}>
              <Cropper
                image={photo}
                crop={crop}
                zoom={zoom}
                rotation={Number(rotation)}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={(z) => setZoom(Number(z))}
                onCropComplete={onCropComplete}
                // react-easy-crop does not have onRotationChange consistently; we control rotation via state input
              />
            </div>

            <div className="w-100 mt-3">
              <label className="form-label mb-1">Zoom</label>
              <input
                type="range"
                className="form-range"
                min={1}
                max={3}
                step={0.01}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
              />

              <label className="form-label mt-2 mb-1">Rotate</label>
              <input
                type="range"
                className="form-range"
                min={0}
                max={360}
                step={1}
                value={rotation}
                onChange={(e) => setRotation(Number(e.target.value))}
              />
            </div>

            <div className="mt-3 d-flex gap-3">
              <button className="btn btn-secondary" onClick={() => setCropMode(false)}>
                Cancel
              </button>
              <button className="btn btn-success" onClick={handleCropSave}>
                Save Crop
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main card */}
      <div className="card shadow-lg border-0 rounded-4 animate__animated animate__fadeIn">
        {/* Header */}
        <div
          className="card-header text-white text-center py-4"
          style={{
            background: "linear-gradient(135deg, #0601208c, #170229ff)",
            borderTopLeftRadius: "1rem",
            borderTopRightRadius: "1rem",
            boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
          }}
        >
          <h3 className="mb-0 fw-bold">
            <i className="bi bi-person-circle me-2"></i> Manage Profile
          </h3>
          <p className="small text-light mb-0">Update your personal info</p>
        </div>

        {/* Body */}
        <div className="card-body p-4">
          {/* Profile Image */}
          <div className="text-center mb-4">
            <div className="position-relative d-inline-block">
              <img
                src={photo || "https://via.placeholder.com/150"}
                alt="Profile"
                className="rounded-circle border border-3 border-success shadow-lg"
                width="140"
                height="140"
                style={{ objectFit: "cover" }}
              />
              <label
                htmlFor="fileUpload"
                className="position-absolute bottom-0 end-0 bg-success text-white rounded-circle p-2 shadow-lg"
                style={{ cursor: "pointer" }}
                title="Change Photo"
              >
                <i className="bi bi-camera-fill"></i>
              </label>
              <input
                id="fileUpload"
                type="file"
                accept="image/*"
                hidden
                onChange={handleFileChange}
              />
            </div>
            <p className="text-muted mt-2 mb-0">Click camera to update photo</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="row">
              {/* Left column */}
              <div className="col-md-6">
                {/* Email */}
                <div className="mb-3">
                  <label className="form-label fw-bold">
                    <i className="bi bi-envelope me-2 text-secondary"></i>Email
                  </label>
                  <input
                    type="email"
                    className="form-control rounded-3 shadow-sm"
                    value={email}
                    readOnly
                  />
                </div>

                {/* Old Password */}
                <div className="mb-3">
                  <label className="form-label fw-bold">
                    <i className="bi bi-lock me-2 text-secondary"></i>Old Password
                  </label>
                  <div className="input-group">
                    <input
                      type={showOldPassword ? "text" : "password"}
                      className="form-control rounded-start-3 shadow-sm"
                      placeholder="Enter old password"
                      value={oldPassword}
                      onChange={(e) => setOldPassword(e.target.value)}
                    />
                    <span
                      className="input-group-text bg-white rounded-end-3"
                      style={{ cursor: "pointer" }}
                      onClick={() => setShowOldPassword(!showOldPassword)}
                    >
                      {showOldPassword ? <FaEye /> : <FaEyeSlash />}
                    </span>
                  </div>
                </div>
              </div>

              {/* Right column */}
              <div className="col-md-6">
                {/* Name */}
                <div className="mb-3">
                  <label className="form-label fw-bold">
                    <i className="bi bi-person me-2 text-secondary"></i>Name
                  </label>
                  <input
                    type="text"
                    className="form-control rounded-3 shadow-sm"
                    placeholder="Enter your full name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>

                {/* New Password */}
                <div className="mb-3">
                  <label className="form-label fw-bold">
                    <i className="bi bi-key me-2 text-secondary"></i>New Password
                  </label>
                  <div className="input-group">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      className="form-control rounded-start-3 shadow-sm"
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                    />
                    <span
                      className="input-group-text bg-white rounded-end-3"
                      style={{ cursor: "pointer" }}
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? <FaEye /> : <FaEyeSlash />}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="d-flex justify-content-between mt-4">
              <button
                type="button"
                className="btn btn-outline-secondary px-4 rounded-3"
                onClick={() => navigate(-1)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-success px-4 rounded-3 shadow-sm"
                disabled={loading}
              >
                {loading ? (
                  <span>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    ></span>
                    Saving...
                  </span>
                ) : (
                  <span>
                    <i className="bi bi-check-circle me-2"></i>Update Profile
                  </span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
    </>
  );
}
